/* eslint-disable padding-line-between-statements */
import { Deck } from '@deck.gl/core';
import FlowMapLayer, { PickingType } from '@flowmap.gl/core';
import { getColors } from '@flowmap.gl/data';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getClusterZoom, getFlowDestId,
    getFlowMagnitude,
    getFlowOriginId, getLocationCentroid, getLocationId, getMixBlendMode,
    isFlowInSelection,
} from './commonGetters';
import { fetchCorrespondingMatrix } from './fetchCorrespondingMatrix';
import { getClusterIndex } from './getClusterIndex';
import { getFlowMagnitudeExtent } from './getFlowMagnitudeExtent';
import { getTooltipState } from './getTooltipState';

export const MapDeck = ({ map, mapStyle, displayedLayers = [] }) => {
    const { correspondingMatrixPeriod } = useSelector(({ mapFilters }) => mapFilters);
    const dispatch = useDispatch();
    const [ correspondingMatrixData, setCorrespondingMatrixData ] = useState(null);
    const [ zoom, setZoom ] = useState(() => map.getZoom());
    const [ tooltip, setTooltip ] = useState(null);
    const [ selectedLocationsSet, setSelectedLocationsSet ] = useState(new Set());
    const containerRef = useRef(null);
    const deckRef = useRef(null);

    const isCorrespondingMatrixVisible = useMemo(
        () => displayedLayers.some(({ id }) => id === 'deck:operation_areas_corresponding_matrix'),
        [ displayedLayers ]
    );

    const clusterIndex = useMemo(() => {
        if (!correspondingMatrixData) {
            return;
        }

        try {
            return getClusterIndex(
                correspondingMatrixData.locations,
                correspondingMatrixData.flows
            );
        } catch (error) {
            console.error('Error creating cluster index:', error);
            return null;
        }
    }, [ correspondingMatrixData ]);

    const flowsForLayer = useMemo(() => {
        if (!clusterIndex || !correspondingMatrixData?.flows) {
            return [];
        }

        try {
            const clusterZoom = getClusterZoom(clusterIndex, zoom, selectedLocationsSet);

            if (clusterZoom === null) {
                console.warn('Invalid cluster zoom level in flowsForLayer:', clusterZoom);
                return [];
            }

            const flows = clusterIndex.aggregateFlows(
                correspondingMatrixData.flows.filter(flow => flow &&
                getFlowOriginId(flow) &&
                getFlowDestId(flow)
                ),
                clusterZoom,
                {
                    getFlowOriginId,
                    getFlowDestId,
                    getFlowMagnitude,
                }
            );

            if (!flows || !Array.isArray(flows)) {
                console.warn('aggregateFlows returned invalid data:', flows);
                return [];
            }

            return flows.filter(flow => flow &&
              isFlowInSelection(flow, selectedLocationsSet) &&
              getFlowOriginId(flow) &&
              getFlowDestId(flow)
            );
        } catch (error) {
            console.error('Error aggregating flows:', error);
            return [];
        }
    }, [ correspondingMatrixData, clusterIndex, selectedLocationsSet, zoom ]);

    const locationsForLayer = useMemo(() => {
        if (!clusterIndex) {
            return [];
        }

        try {
            const clusterZoom = getClusterZoom(clusterIndex, zoom, selectedLocationsSet);

            if (clusterZoom === null) {
                console.warn('Invalid cluster zoom level:', clusterZoom);
                return [];
            }

            const locations = clusterIndex.getClusterNodesFor(clusterZoom);

            if (!locations || !Array.isArray(locations)) {
                console.warn('No locations found for cluster zoom level:', clusterZoom);
                return [];
            }

            const withFlows = new Set();

            for (const flow of flowsForLayer) {
                withFlows.add(getFlowOriginId(flow));
                withFlows.add(getFlowDestId(flow));
            }

            return locations.filter(location => {
                const locationId = getLocationId(location);
                const centroid = getLocationCentroid(location);
                return locationId && centroid && withFlows.has(locationId);
            });
        } catch (error) {
            console.error('Error getting locations for layer:', error);
            return [];
        }
    }, [ clusterIndex, flowsForLayer, selectedLocationsSet, zoom ]);


    const flowMagnitudeExtent = useMemo(() => {
        return getFlowMagnitudeExtent(flowsForLayer, selectedLocationsSet);
    }, [ flowsForLayer, selectedLocationsSet ]);

    const layers = useMemo(() => {
        // Additional validation to ensure data consistency
        if (flowsForLayer.length > 0 && locationsForLayer.length === 0) {
            console.warn('Flows exist but no locations available', {
                flowsCount: flowsForLayer.length,
                locationsCount: locationsForLayer.length,
                zoom,
                selectedLocationsCount: selectedLocationsSet.size,
                clusterIndexExists: !!clusterIndex,
            });
            return [];
        }

        if (!isCorrespondingMatrixVisible || (flowsForLayer.length === 0 && locationsForLayer.length === 0)) {
            return [];
        }

        return [
            new FlowMapLayer({
                visible: isCorrespondingMatrixVisible,
                locations: locationsForLayer,
                flows: flowsForLayer,
                colors: getColors(false, 'Blues', true, true, 35, false),
                pickable: true,
                highlightedLocationId: tooltip?.info.type === PickingType.LOCATION
                    ? tooltip.info.object?.id
                    : null,
                highlightedFlow: tooltip?.info.type === PickingType.FLOW
                    ? tooltip.info.object
                    : null,
                maxFlowThickness: 12,
                flowMagnitudeExtent,
                getLocationId,
                getLocationCentroid,
                getFlowOriginId,
                getFlowDestId,
                getFlowMagnitude,
                onHover: info => {
                    try {
                        const deckTooltipState = getTooltipState(info);
                        setTooltip(deckTooltipState);
                    } catch (error) {
                        console.error('Error in hover handler:', error);
                        setTooltip(null);
                    }
                },
                onClick: (info, event) => {
                    try {
                        switch (info.type) {
                            case PickingType.LOCATION:
                            case PickingType.LOCATION_AREA: {
                                if (info.object) {
                                    const id = getLocationId(info.object);
                                    if (id) {
                                        setSelectedLocationsSet(prev => {
                                            const next = new Set(prev);

                                            if (prev.has(id)) {
                                                next.delete(id);
                                            } else {
                                                if (!event.srcEvent.shiftKey) {
                                                    next.clear();
                                                }

                                                next.add(id);
                                            }

                                            return next;
                                        });
                                    }
                                }
                                break;
                            }
                            default: break;
                        }
                    } catch (error) {
                        console.error('Error in click handler:', error);
                    }
                },
            }),
        ];
    }, [ flowsForLayer, locationsForLayer, isCorrespondingMatrixVisible, tooltip, flowMagnitudeExtent, zoom, selectedLocationsSet.size, clusterIndex ]);

    useEffect(() => {
        let mutableViewState = {
            longitude: map.getCenter().lng,
            latitude: map.getCenter().lat,
            zoom: map.getZoom(),
            bearing: map.getBearing(),
            pitch: map.getPitch(),
        };

        const deck = new Deck({
            map: true,
            controller: true,
            parent: containerRef.current,
            initialViewState: mutableViewState,
            onViewStateChange: ({ viewState }) => {
                mutableViewState = viewState;
                setZoom(viewState.zoom);
                map.jumpTo({
                    center: [ viewState.longitude, viewState.latitude ],
                    zoom: viewState.zoom,
                    bearing: viewState.bearing,
                    pitch: viewState.pitch,
                });
            },
        });

        map.on('dragend', () => {
            deck.setProps({
                initialViewState: {
                    ...mutableViewState,
                    longitude: map.getCenter().lng,
                    latitude: map.getCenter().lat,
                },
            });
        });

        map.on('moveend', () => {
            deck.setProps({
                initialViewState: {
                    ...mutableViewState,
                    longitude: map.getCenter().lng,
                    latitude: map.getCenter().lat,
                },
            });
        });

        map.on('pitchend', () => {
            deck.setProps({
                initialViewState: {
                    ...mutableViewState,
                    pitch: map.getPitch(),
                },
            });
        });

        map.on('zoomend', () => {
            deck.setProps({
                initialViewState: {
                    ...mutableViewState,
                    zoom: map.getZoom(),
                },
            });
        });

        map.on('rotateend', () => {
            deck.setProps({
                initialViewState: {
                    ...mutableViewState,
                    bearing: map.getBearing(),
                },
            });
        });

        deckRef.current = deck;
    }, [ map ]);

    useEffect(() => {
        if (!deckRef.current) {
            return;
        }

        deckRef.current.setProps({ layers });
    }, [ layers ]);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        containerRef.current.style.mixBlendMode = getMixBlendMode(mapStyle);
    }, [ mapStyle ]);

    useEffect(() => {
        if (!isCorrespondingMatrixVisible) {
            return;
        }

        fetchCorrespondingMatrix(correspondingMatrixPeriod, setCorrespondingMatrixData, dispatch);
    }, [ correspondingMatrixPeriod, isCorrespondingMatrixVisible, dispatch ]);

    return (
        <div
            ref={containerRef}
            style={{
                display: isCorrespondingMatrixVisible ? 'block' : 'none',
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                overflow: 'hidden',
            }}
        >
            {tooltip && (
                <div
                    className="deck__tooltip"
                    style={tooltip.position}
                >
                    {tooltip.content}
                </div>
            )}
        </div>
    );
};
